import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { PricingUpdateForm } from '../../components/forms/PricingUpdateForm';
import { PricingUpdatePrfDropzone } from '../../components/forms/PricingUpdatePrfDropzone';
import { PageNotificationToast } from '../../components/notifications/PageNotificationToast';
import { usePageNotifications } from '../../hooks/usePageNotifications';
import { pageFeedbackToToast } from '../../lib/pageFeedbackToast';
import { resolveInternalSubmitterLabel } from '../../lib/applicationFormSubmitter';
import {
  formatPricingUpdateNotifySummary,
  notifyPricingUpdateSubmitted,
} from '../../lib/pricingUpdateNotifications';
import {
  formatPrfImportSummary,
  parsePriceReviewPdfFile,
} from '../../lib/pricingUpdatePrfImport';
import {
  normalizePricingUpdateFormData,
  patchPricingUpdateRequestorAttachments,
  submitPricingUpdate,
  uploadPricingUpdateAttachment,
  validatePricingUpdateRequestor,
} from '../../lib/pricingUpdateSubmissions';
import { useUnifiedPortalUserLookup } from '../../hooks/useUnifiedPortalUserLookup';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import {
  createPricingUpdateItemRow,
  EMPTY_PRICING_UPDATE_FORM,
  type PricingUpdateFormData,
} from '../../types/applicationForm';

export type PricingUpdateNewNavigateState = {
  pufNotice?: string;
  pufError?: string;
};

export function PricingUpdateNewPage() {
  const { basePath, kind } = usePortal();
  const isDem = kind === 'dem';
  const navigate = useNavigate();
  const { profile, authUser } = useAuth();
  const { rows: unifiedUsers } = useUnifiedPortalUserLookup();
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState<PricingUpdateFormData>(() => ({
    ...EMPTY_PRICING_UPDATE_FORM,
    items: [createPricingUpdateItemRow()],
  }));
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const lastErrorToastRef = useRef<string | null>(null);
  const lastNoticeToastRef = useRef<string | null>(null);
  const { push: pushToast, activeToast, dismissToast } = usePageNotifications();

  useEffect(() => {
    if (error && error !== lastErrorToastRef.current) {
      lastErrorToastRef.current = error;
      pushToast(pageFeedbackToToast({ kind: 'error', message: error }));
    }
    if (!error) lastErrorToastRef.current = null;
  }, [error, pushToast]);

  useEffect(() => {
    if (notice && notice !== lastNoticeToastRef.current) {
      lastNoticeToastRef.current = notice;
      pushToast(pageFeedbackToToast({ kind: 'success', message: notice }));
    }
    if (!notice) lastNoticeToastRef.current = null;
  }, [notice, pushToast]);

  const listPath = basePath ? `${basePath}/pricing-update` : '/pricing-update';

  const submitterLabel = useMemo(
    () => resolveInternalSubmitterLabel(profile, authUser, unifiedUsers) ?? '',
    [profile, authUser, unifiedUsers],
  );

  const handlePrfImport = async (file: File) => {
    setImporting(true);
    setError(null);
    setNotice(null);
    try {
      const result = await parsePriceReviewPdfFile(file);
      setForm((prev) =>
        normalizePricingUpdateFormData({
          ...prev,
          ...result.formPatch,
          reason_requestor: '',
          items:
            result.imported.length > 0 ? result.imported : [createPricingUpdateItemRow()],
        }),
      );
      setPendingFiles((prev) => {
        const withoutSame = prev.filter(
          (existing) =>
            existing.name !== file.name || existing.size !== file.size || existing.lastModified !== file.lastModified,
        );
        return [...withoutSame, file];
      });
      setNotice(formatPrfImportSummary(result));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import Price Review PDF.');
    } finally {
      setImporting(false);
    }
  };

  const handleSubmit = async () => {
    const validationError = validatePricingUpdateRequestor(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const payload = normalizePricingUpdateFormData({
        ...form,
        requestor_attachments: [],
      });
      const row = await submitPricingUpdate(
        payload,
        submitterLabel || undefined,
        authUser?.email ?? profile?.email ?? undefined,
      );

      if (pendingFiles.length > 0) {
        const uploaded = [];
        for (const file of pendingFiles) {
          uploaded.push(await uploadPricingUpdateAttachment(row.id, 'requestor', file));
        }
        const withFiles = normalizePricingUpdateFormData({
          ...(row.form_data as PricingUpdateFormData),
          requestor_attachments: uploaded,
        });
        await patchPricingUpdateRequestorAttachments(row.id, withFiles);
      }

      const notifyResult = await notifyPricingUpdateSubmitted(row);
      const summary = await formatPricingUpdateNotifySummary(notifyResult);
      const state: PricingUpdateNewNavigateState = {
        pufNotice: `Submitted. ${summary}`,
      };
      if (!notifyResult.ok || notifyResult.sent.length === 0) {
        state.pufError =
          'Add Authorised / Approver emails in Forms settings → Pricing Update so they get notified.';
      }
      navigate(`${listPath}/${row.id}`, { replace: true, state });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit.');
    } finally {
      setBusy(false);
    }
  };

  const pageClass = `af-applications-page af-form-page${isDem ? ' puf-page--floating' : ''}`;

  return (
    <div className={pageClass}>
      {isDem ? (
        <Link to={listPath} className="btn-secondary delivery-page__back">
          Back to list
        </Link>
      ) : (
        <header className="page-header">
          <div className="page-header__text">
            <h1 className="page-title">New Pricing Update</h1>
          </div>
          <div className="page-header__actions">
            <Link to={listPath} className="btn-secondary btn-sm">
              Back to list
            </Link>
          </div>
        </header>
      )}

      {activeToast
        ? createPortal(
            <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
            document.body,
          )
        : null}

      <PricingUpdatePrfDropzone
        disabled={busy}
        busy={importing}
        onFile={(file) => void handlePrfImport(file)}
      />

      <PricingUpdateForm
        mode="new"
        value={form}
        onChange={setForm}
        requestorEditable
        authorisedEditable={false}
        showAdminSection={false}
        pendingRequestorFiles={pendingFiles}
        onPendingRequestorFilesChange={setPendingFiles}
      />
      <div className="puf-form__actions puf-form__actions--bar">
        <button
          type="button"
          className="btn-primary"
          disabled={busy || importing}
          onClick={() => void handleSubmit()}
        >
          {busy ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </div>
  );
}
